(() => {
  if (window.VCEssenceRenderer) return;
  const cache = new Map();
  const image = url => {
    if (!cache.has(url)) cache.set(url, new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => { cache.delete(url); reject(new Error('Image unavailable')); };
      img.src = url;
    }));
    return cache.get(url);
  };
  const normalize = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const metalIndex = value => ({chrome:1,'brushed-nickel':2,gold:3,'brushed-gold':3,'matte-black':0,black:0})[normalize(value)];
  class Renderer {
    constructor(canvas, data) { this.canvas = canvas; this.data = data; this.revision = 0; }
    async render(entry, top, hardware) {
      const revision = ++this.revision;
      let finish = normalize(top);
      if (finish === 'carrara' || finish === 'carrera') finish = 'carrara-white';
      if (finish === 'golden-veil') finish = 'calacatta-gold';
      const metal = metalIndex(hardware);
      if (metal === undefined || (finish !== 'pure-white' && !this.data.textures[finish])) throw new Error('Unsupported finish');
      const [base, mask, atlas, texture] = await Promise.all([
        image(entry.base), entry.mask ? image(entry.mask) : Promise.resolve(null), image(this.data.atlas),
        finish === 'pure-white' ? Promise.resolve(null) : image(this.data.textures[finish])
      ]);
      if (revision !== this.revision) return false;
      const off = document.createElement('canvas'); off.width = off.height = 1254;
      const ctx = off.getContext('2d'); ctx.drawImage(base,0,0,1254,1254);
      if (entry.tint && entry.cabinet) {
        const [x,y,w,h]=entry.cabinet;
        const pixels=ctx.getImageData(x,y,w,h), rgba=pixels.data;
        for(let i=0;i<rgba.length;i+=4){
          const shade=((rgba[i]+rgba[i+1]+rgba[i+2])/3-235)*.85;
          for(let channel=0;channel<3;channel++)rgba[i+channel]=Math.max(0,Math.min(255,entry.tint[channel]+shade));
        }
        ctx.putImageData(pixels,x,y);
      }
      if (texture) {
        const layer = document.createElement('canvas'); layer.width = layer.height = 1254;
        const lc = layer.getContext('2d');
        const [bl,br,by,dy,fl,fr,fy,bottom] = entry.geometry;
        lc.drawImage(texture,0,0,1080,180,bl,by,br-bl,dy-by);
        for (let y=dy; y<fy; y++) {
          const f=(y-dy)/(fy-dy), left=bl+(fl-bl)*f, right=br+(fr-br)*f;
          lc.drawImage(texture,0,180+f*430,1080,Math.max(1,430/(fy-dy)),left,y,right-left,1.1);
        }
        lc.drawImage(texture,0,610,1080,70,fl,fy,fr-fl,bottom-fy);
        lc.globalCompositeOperation='destination-in';
        if(mask){lc.drawImage(mask,0,0,1254,1254);}else{
          const shape=document.createElement('canvas');shape.width=shape.height=1254;
          const sc=shape.getContext('2d');sc.fillStyle='#fff';
          sc.beginPath();sc.moveTo(bl,by);sc.lineTo(br,by);sc.lineTo(br,dy);sc.lineTo(fr,fy);sc.lineTo(fr,bottom);sc.lineTo(fl,bottom);sc.lineTo(fl,fy);sc.lineTo(bl,dy);sc.closePath();sc.fill();
          sc.globalCompositeOperation='destination-out';
          for(const points of entry.cutouts||[]){sc.beginPath();points.forEach(([x,y],i)=>i?sc.lineTo(x,y):sc.moveTo(x,y));sc.closePath();sc.fill();}
          lc.drawImage(shape,0,0);
        }
        ctx.globalCompositeOperation='multiply'; ctx.globalAlpha=.92; ctx.drawImage(layer,0,0);
        ctx.globalCompositeOperation='source-over'; ctx.globalAlpha=1;
      }
      for (const p of entry.hardware) {
        if(p.kind==='horizontal'){
          ctx.save();ctx.translate(p.x+p.w,p.y);ctx.rotate(Math.PI/2);
          ctx.drawImage(atlas,metal*64,0,23,163,0,0,p.h,p.w);ctx.restore();continue;
        }
        const knob = p.kind === 'knob';
        ctx.drawImage(atlas,metal*64,knob?180:0,knob?33:23,knob?35:163,p.x,p.y,p.w,p.h);
      }
      this.canvas.width=this.canvas.height=1254;
      this.canvas.getContext('2d').drawImage(off,0,0);
      this.canvas.dataset.finish=finish;this.canvas.dataset.hardware=normalize(hardware);this.canvas.dataset.source=entry.name;
      return true;
    }
  }
  window.VCEssenceRenderer=Renderer;
  async function mount(root) {
    if (root.dataset.mounted) return;
    root.dataset.mounted='true';
    const config=JSON.parse(root.querySelector('[data-essence-config]').textContent);
    const gallery=root.closest('.gallery'), section=root.closest('[data-section-type="main-product"]');
    const hardwareControl=section.querySelector(`.option-selector[data-option-index="${config.hardwareIndex}"]`);
    const topControl=section.querySelector(`.option-selector[data-option-index="${config.topIndex}"]`);
    if(hardwareControl && topControl && hardwareControl.parentNode===topControl.parentNode){
      const group=topControl.parentNode;
      group.style.display='flex';group.style.flexDirection='column';
      group.querySelectorAll('.option-selector').forEach(control=>{
        control.style.order=control===hardwareControl?'1':control===topControl?'2':'0';
      });
    }
    const canvas=root.querySelector('canvas'), caption=root.querySelector('[data-essence-caption]');
    const status=root.querySelector('[data-essence-status]');
    let manifest, renderer, last='', sequence=0;
    let navigationRevision=0, interacted=false;
    const mode=value=>{
      gallery.dataset.essenceMode=value;
      if(value==='photos')requestAnimationFrame(()=>{
        window.dispatchEvent(new Event('resize'));
        const slider=gallery.querySelector('.slick-initialized');
        if(slider?.slick)slider.slick.setPosition();
      });
    };
    mode('photos');
    gallery.addEventListener('click',event=>{
      if(event.target.closest('.thumbnail,.slideshow-controls,.slick-arrow')){
        navigationRevision++;
        mode('photos');
      }
    },true);
    const options=()=>config.options.map((fallback,i)=>{
      const control=section.querySelector(`.option-selector[data-option-index="${i}"]`);
      return control?.querySelector('input:checked')?.value || control?.querySelector('select')?.value || fallback;
    });
    const listener=event=>{
      if(event.type==='change'){
        if(!event.target.closest('.option-selector'))return;
        if(event.isTrusted)interacted=true;
      }
      setTimeout(()=>update(true),0);
    };
    section.addEventListener('change',listener,true);
    window.addEventListener('popstate',listener);
    async function update(force=false) {
      if (!renderer || !root.isConnected) return;
      const values=options(); const key=JSON.stringify(values);
      if (!force && key===last) return;last=key;
      const run=++sequence, navigationAtStart=navigationRevision;
      const showPreview=interacted;
      const cabinet=values[config.cabinetIndex], top=values[config.topIndex], hardware=values[config.hardwareIndex];
      const entry=manifest.products[config.productId]?.[cabinet];
      root.setAttribute('aria-busy','true');
      try {
        if (!entry) throw new Error('Cabinet unavailable');
        const rendered=await renderer.render(entry,top,hardware);
        if (!rendered || run!==sequence) return;
        const label=[cabinet,top,hardware==='Gold'?'Brushed Gold':hardware].map(value=>String(value).replace(/\b[a-z]/g,letter=>letter.toUpperCase())).join(' · ');
        canvas.setAttribute('aria-label',config.title+' — '+label);caption.textContent=label;
        status.textContent='';gallery.classList.add('vc-essence-ready');
        if(showPreview && navigationAtStart===navigationRevision)mode('preview');
      } catch (error) {
        if(run!==sequence)return;
        gallery.classList.remove('vc-essence-ready');status.textContent=config.error;
      } finally {if(run===sequence)root.setAttribute('aria-busy','false');}
    }
    try {
      const response=await fetch(config.manifestUrl);if(!response.ok)throw new Error('Manifest unavailable');
      manifest=await response.json();renderer=new Renderer(canvas,manifest);await update(true);
      const timer=setInterval(()=>{if(!root.isConnected){clearInterval(timer);section.removeEventListener('change',listener,true);window.removeEventListener('popstate',listener);}else update();},600);
    } catch(error){status.textContent=config.error;}
    const dialog=root.querySelector('dialog');
    root.querySelector('[data-essence-zoom]').addEventListener('click',()=>{
      const large=dialog.querySelector('canvas');large.width=large.height=1254;large.getContext('2d').drawImage(canvas,0,0);
      large.setAttribute('aria-label',canvas.getAttribute('aria-label'));dialog.showModal();
    });
    dialog.querySelector('button').addEventListener('click',()=>dialog.close());
    dialog.addEventListener('click',e=>{if(e.target===dialog)dialog.close();});
  }
  const initialize=()=>document.querySelectorAll('[data-essence-preview]').forEach(mount);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initialize);else initialize();
  document.addEventListener('shopify:section:load',initialize);
})();
